// Small date helpers. We deliberately avoid `new Date("2026-01-01")` + toISOString()
// for date-only values: that parses as UTC midnight and, in PH (UTC+8), shifts the
// day backwards. These helpers work in local time so a "2026-01-01" stays Jan 1.

/** Format a Date as a local YYYY-MM-DD string (no timezone shift). */
export function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Parse a YYYY-MM-DD string into a local Date at midnight. */
export function parseISODate(iso: string): Date {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d);
}

/** Today as YYYY-MM-DD (local). */
export function todayISO(): string {
  return toISODate(new Date());
}

/**
 * Normalize a Graph/SharePoint date or datetime value to a local YYYY-MM-DD.
 * SharePoint stores date-only columns at midnight *site* time, and Graph
 * returns them shifted to UTC (a PH-site "Aug 10" comes back as
 * "2026-08-09T16:00:00Z") — so taking the raw substring loses a day. Converting
 * through the browser's local timezone (same as the site's for KTI) recovers
 * the intended calendar date.
 */
export function isoToLocalDate(value?: string): string {
  if (!value) return "";
  const s = String(value);
  if (s.length <= 10) return s; // already a bare date
  return toISODate(new Date(s));
}

/** Add `days` calendar days to a YYYY-MM-DD string, returning YYYY-MM-DD. */
export function addDays(iso: string, days: number): string {
  const d = parseISODate(iso);
  d.setDate(d.getDate() + days);
  return toISODate(d);
}

/**
 * Add calendar months to a YYYY-MM-DD string. Clamps to the end of shorter
 * months: Jan 31 + 1 month = Feb 28 (not Mar 3).
 */
export function addMonths(iso: string, months: number): string {
  const d = parseISODate(iso);
  const target = new Date(d.getFullYear(), d.getMonth() + months, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(d.getDate(), lastDay));
  return toISODate(target);
}

/** Whole calendar days between two YYYY-MM-DD strings (b - a). */
export function daysBetween(aIso: string, bIso: string): number {
  const a = parseISODate(aIso).getTime();
  const b = parseISODate(bIso).getTime();
  return Math.round((b - a) / 86_400_000);
}

/** "Jul 14, 2026" */
export function formatDisplayDate(iso?: string): string {
  if (!iso) return "";
  return parseISODate(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
}

/** "Wed, Sep 16, 2026" */
export function formatLongDate(iso?: string): string {
  if (!iso) return "";
  return parseISODate(iso).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Parse a stored list of leave dates ("2026-09-21,2026-09-23") into sorted,
 * de-duplicated YYYY-MM-DD strings. Tolerates spaces/semicolons/newlines.
 */
export function parseDateList(value?: string | null): string[] {
  if (!value) return [];
  const dates = String(value)
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter((s) => /^\d{4}-\d{2}-\d{2}$/.test(s));
  return Array.from(new Set(dates)).sort();
}

/** Split sorted YYYY-MM-DD dates into runs of consecutive calendar days. */
export function groupConsecutiveDates(dates: string[]): string[][] {
  const runs: string[][] = [];
  for (const d of [...dates].sort()) {
    const run = runs[runs.length - 1];
    if (run && addDays(run[run.length - 1], 1) === d) run.push(d);
    else runs.push([d]);
  }
  return runs;
}

/**
 * Compact label for a set of leave dates, e.g.
 *   ["2026-09-21","2026-09-22","2026-09-23","2026-09-25"] -> "Sep 21–23, 25, 2026"
 */
export function formatDateList(dates: string[]): string {
  if (dates.length === 0) return "";
  const runs = groupConsecutiveDates(dates);
  const years = new Set(dates.map((d) => d.slice(0, 4)));
  if (years.size > 1) {
    return runs
      .map((r) =>
        r.length === 1
          ? formatDisplayDate(r[0])
          : `${formatDisplayDate(r[0])} – ${formatDisplayDate(r[r.length - 1])}`,
      )
      .join(", ");
  }
  const month = (iso: string) =>
    parseISODate(iso).toLocaleDateString("en-US", { month: "short" });
  const day = (iso: string) => String(parseISODate(iso).getDate());
  let lastMonth = "";
  const parts = runs.map((r) => {
    const first = r[0];
    const last = r[r.length - 1];
    const startLabel = month(first) === lastMonth ? day(first) : `${month(first)} ${day(first)}`;
    lastMonth = month(last);
    if (r.length === 1) return startLabel;
    const endLabel = month(last) === month(first) ? day(last) : `${month(last)} ${day(last)}`;
    return `${startLabel}–${endLabel}`;
  });
  return `${parts.join(", ")}, ${dates[0].slice(0, 4)}`;
}
